import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";

/**
 * Öffentliche Unsubscribe-Seite. Kein Login erforderlich, kein CSRF.
 * Zweistufig: Anzeige, welche E-Mail widerrufen wird → Bestätigen-Button.
 * Preview-Token (aus Vorschau/Test-Mail) tut nichts, zeigt Hinweis.
 */
export default function Unsubscribe() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const isPreview = params.get("preview") === "1" || token === "preview";

  const [state, setState] = useState({ loading: true, info: null, error: null, done: false });
  const [busy, setBusy] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const client = axios.create({ baseURL: `${backend}/api`, withCredentials: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await client.get(`/public/newsletter/unsubscribe/${encodeURIComponent(token)}`);
        if (alive) setState((s) => ({ ...s, loading: false, info: r.data }));
      } catch (e) {
        if (alive) setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || "Ungültiger Abmelde-Link" }));
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      await client.post(`/public/newsletter/unsubscribe/${encodeURIComponent(token)}`);
      setState((s) => ({ ...s, done: true }));
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Abmelden fehlgeschlagen" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-950 text-zinc-100 flex items-center justify-center p-4" data-testid="unsubscribe-page">
      <div className="w-full max-w-md border border-zinc-800 rounded-sm bg-zinc-900 p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-4">
          <Mail size={18} className="text-cyan-400" />
          <div className="font-black uppercase tracking-widest text-sm">Newsletter</div>
        </div>
        <h1 className="font-display text-2xl font-black uppercase tracking-tight mb-2">Vom Newsletter abmelden</h1>

        {state.loading && (
          <div className="text-sm text-zinc-400 mt-4" data-testid="unsubscribe-loading">Lade…</div>
        )}

        {!state.loading && state.error && (
          <div className="mt-4 p-3 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2" data-testid="unsubscribe-error">
            <AlertTriangle size={16} /> {state.error}
          </div>
        )}

        {!state.loading && !state.error && isPreview && (
          <div className="mt-4 p-3 rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm" data-testid="unsubscribe-preview">
            Dies ist ein Vorschau-/Test-Link. Es wird kein Abmelde-Vorgang ausgelöst.
          </div>
        )}

        {!state.loading && !state.error && !isPreview && state.info && !state.done && (
          <div className="space-y-4" data-testid="unsubscribe-form">
            <p className="text-sm text-zinc-300">
              Du bist dabei, dich mit
              <span className="mx-1 font-mono text-cyan-300">{state.info.email}</span>
              vom Newsletter abzumelden. System-Mails (Passwort-Reset, E-Mail-Verifikation) bleiben davon unberührt.
            </p>
            {state.info.already_revoked && (
              <div className="p-3 rounded-sm bg-zinc-800/50 border border-zinc-700 text-zinc-300 text-sm">
                Hinweis: Für diese Adresse ist bereits kein Marketing-Consent mehr aktiv. Ein erneuter Klick ändert daran nichts.
              </div>
            )}
            <Button data-testid="unsubscribe-confirm-btn" onClick={confirm} disabled={busy} className="w-full bg-cyan-400 hover:bg-cyan-300 text-zinc-950 h-11">
              {busy ? "Bitte warten…" : "Abmelden bestätigen"}
            </Button>
          </div>
        )}

        {state.done && (
          <div className="mt-4 p-3 rounded-sm bg-green-500/10 border border-green-500/30 text-green-300 text-sm flex items-center gap-2" data-testid="unsubscribe-done">
            <CheckCircle2 size={16} /> Du wurdest erfolgreich vom Newsletter abgemeldet. Danke für dein Feedback.
          </div>
        )}
      </div>
    </div>
  );
}
